import React, { useEffect, useRef } from 'react';
import { TouchableOpacity, View, Text, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  refreshUnreadCount,
  selectBadgeText,
  selectUnreadCount,
} from '../notificationsSlice';
import { selectIsAuthenticated } from '../../auth/authSlice';

/**
 * NotificationBell — Sprint 8 / FE-127.
 *
 * Top-bar bell icon (rendered to the LEFT of the profile avatar per
 * the user's request). Shows a red dot + numeric badge when the
 * server-side unread count is > 0. Tapping navigates to the
 * NotificationsScreen route registered in MainAppNavigator.
 *
 * Polling cadence:
 *   - 60s while the user is authenticated and the app is foregrounded
 *   - Single immediate fetch on first mount (no badge flicker)
 *   - Stops when the user logs out (auth selector flips false)
 *
 * The interval is intentionally simple (setInterval, not visibility-
 * aware): the unread-count endpoint is one indexed SQL COUNT —
 * negligible server cost even at 1 req/min per user. Visibility-
 * aware polling can layer on later without changing the contract.
 *
 * The cache stays warm across NotificationsScreen → back navigation
 * because both surfaces read from the same Redux slice. The bell's
 * polling will continue refreshing the count while the screen is
 * open, so the badge stays consistent with the list.
 */
interface NotificationBellProps {
  /** React Navigation prop — passed from HeaderButtons. */
  onPress: () => void;
  /** Accessibility hint override (rarely needed). */
  accessibilityLabel?: string;
}

const POLL_INTERVAL_MS = 60_000;

export const NotificationBell: React.FC<NotificationBellProps> = ({
  onPress,
  accessibilityLabel,
}) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const count = useAppSelector(selectUnreadCount);
  const badgeText = useAppSelector(selectBadgeText);

  // Track the interval handle so we can clean up on unmount + auth flip.
  const pollHandle = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (pollHandle.current) {
        clearInterval(pollHandle.current);
        pollHandle.current = null;
      }
      return;
    }

    // Initial fetch — fires immediately so the badge isn't blank
    // on the user's first paint.
    dispatch(refreshUnreadCount());

    pollHandle.current = setInterval(() => {
      dispatch(refreshUnreadCount());
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollHandle.current) {
        clearInterval(pollHandle.current);
        pollHandle.current = null;
      }
    };
  }, [dispatch, isAuthenticated]);

  // Use the filled bell when there's anything unread (Material
  // typically maps "notifications" = filled, "notifications-none" =
  // outlined). Keeps the affordance visually noisy ONLY when there's
  // something to look at — matches the iOS / Stitch pattern.
  const hasUnread = count > 0;
  const iconName: keyof typeof MaterialIcons.glyphMap = hasUnread
    ? 'notifications'
    : 'notifications-none';

  const a11yLabel =
    accessibilityLabel ??
    (hasUnread
      ? `Notifications, ${count} unread`
      : 'Notifications, none unread');

  return (
    <TouchableOpacity
      onPress={onPress}
      style={{
        padding: 8,
        marginRight: 4,
        position: 'relative',
      }}
      testID="notification-bell"
      accessibilityRole="button"
      accessibilityLabel={a11yLabel}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      <MaterialIcons
        name={iconName}
        size={22}
        color={hasUnread ? fpColors.primary : fpColors.onSurface}
      />
      {hasUnread && (
        <View
          testID="notification-badge"
          style={{
            position: 'absolute',
            top: 4,
            right: 2,
            minWidth: badgeText && badgeText.length > 1 ? 18 : 10,
            height: badgeText && badgeText.length > 1 ? 14 : 10,
            borderRadius: badgeText && badgeText.length > 1 ? 7 : 5,
            backgroundColor: fpColors.error,
            borderWidth: 1.5,
            borderColor: fpColors.surface,
            justifyContent: 'center',
            alignItems: 'center',
            paddingHorizontal:
              badgeText && badgeText.length > 1 ? 3 : 0,
          }}
        >
          {badgeText && badgeText.length > 1 && (
            <Text
              style={{
                color: fpColors.onError,
                fontSize: 9,
                fontFamily:
                  Platform.OS === 'web' ? 'Inter_700Bold' : undefined,
                fontWeight: '700',
                lineHeight: 11,
              }}
            >
              {badgeText}
            </Text>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
};
